import type { CapabilityId } from '@planuze/config';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';
import { TARGETED_PLANS_CONTENT, type TargetedPlansContent } from './targeted-plans.js';

export type {
  TargetedPlanSectionContent,
  TargetedPlansContent,
  TargetedPlanTableContent,
} from './targeted-plans.js';

/* Copy do catálogo de planos (ADR-0704/0419/0435/0653/0713): header + botões de criação (por
   aba) + as abas Planos/Negociados (`PublicSection`/`TargetedSection`), a tabela de cada aba, o
   modal de edição, o editor de capabilities, o `OrgPicker` e os grupos de campo reusados pelas
   páginas de criação (`plan-form-fields/*`). Os textos duplicados entre o modal de edição e os
   grupos de campo (ex.: "Tier"/"Ordem (rank)"/o toggle "Público no app") ficam em `common` — é a
   MESMA string na tela, não uma coincidência a preservar separadamente. */
export interface PlansContent extends TargetedPlansContent {
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly newPlanButton: string;
  readonly newTargetedPlanButton: string;
  readonly plansTab: string;
  readonly negotiatedTab: string;
  readonly common: {
    readonly tier: string;
    readonly tierPlaceholder: string;
    readonly rank: string;
    readonly lookupKey: string;
    readonly capabilities: string;
    readonly cancel: string;
    readonly save: string;
    readonly archive: string;
    readonly loadMore: string;
    readonly searchPlaceholder: string;
    readonly tierFilterLabel: string;
    readonly interval: string;
    readonly intervalMonthly: string;
    readonly intervalYearly: string;
    readonly statusFilterAriaLabel: string;
    readonly all: string;
    readonly publicToggleLabel: string;
    readonly publicToggleDescription: string;
    readonly clearFilters: string;
  };
  /** Rótulos por id de capability (ADR-0419) — chave fechada em `CapabilityId`. */
  readonly capabilitiesEditor: Readonly<Record<CapabilityId, string>>;
  readonly archiveDialog: {
    readonly confirm: string;
  };
  readonly orgPicker: {
    readonly selectPlaceholder: string;
    readonly searchPlaceholder: string;
    readonly searchAriaLabel: string;
  };
  readonly editModal: {
    readonly updatedToast: string;
    readonly title: string;
    readonly description: string;
    readonly immutableNotice: string;
    readonly targetedTitle: string;
    readonly targetedDescription: string;
    readonly targetedImmutableNotice: string;
    readonly targetedReasonLabel: string;
    readonly targetedReasonError: string;
  };
  readonly publicTable: {
    /** Contador "N capability/capabilities" — pt-BR mantém o termo em inglês (já era assim
        antes da migração; equivalência de força aqui seria inventar texto que a tela nunca
        teve). */
    readonly capabilitySummary: (count: number) => string;
    readonly columnPlan: string;
    readonly columnLookupKey: string;
    readonly columnTier: string;
    readonly columnVisibility: string;
    readonly columnPrice: string;
    readonly columnInterval: string;
    readonly columnCapabilities: string;
    readonly columnActions: string;
    readonly archivedBadge: string;
    readonly visible: string;
    readonly hidden: string;
    readonly editAriaLabel: (planName: string) => string;
    readonly archiveAriaLabel: (planName: string) => string;
    readonly emptyFilteredTitle: string;
    readonly emptyFilteredDescription: string;
    readonly emptyTitle: string;
  };
  readonly publicSection: {
    readonly intro: string;
    readonly searchAriaLabel: string;
    readonly visibilityFilterAriaLabel: string;
    readonly visibilityPublic: string;
    readonly visibilityHidden: string;
    readonly intervalFilterAriaLabel: string;
    readonly statusActive: string;
    readonly statusArchived: string;
    readonly loadError: string;
    readonly archivedToast: string;
    readonly archiveDialogTitle: string;
    readonly archiveDialogDescription: (planName: string) => string;
    readonly bulkSelected: (count: number) => string;
    readonly bulkArchive: string;
    readonly bulkClear: string;
    readonly bulkTitle: string;
    readonly bulkDescription: (count: number) => string;
    readonly bulkConfirm: string;
    readonly bulkSuccess: (count: number) => string;
    readonly bulkPartial: (success: number, failed: number) => string;
    readonly bulkError: string;
    readonly selectPageAriaLabel: string;
    readonly selectRowAriaLabel: (planName: string) => string;
  };
  readonly formFields: {
    readonly displayName: string;
    readonly displayNamePlaceholder: string;
    readonly lookupKeyPlaceholder: string;
    readonly basePrice: string;
    readonly price: string;
    readonly basePriceHint: string;
    readonly seatsIncluded: string;
    readonly extraSeatPrice: string;
    readonly extraSeatHint: string;
    readonly targeting: string;
    readonly targetType: string;
    readonly organization: string;
    readonly user: string;
    readonly target: string;
    readonly validFrom: string;
    readonly validUntil: string;
    readonly reason: string;
    readonly inheritTitle: string;
    readonly inheritDescription: string;
    readonly noSourcePlans: string;
    readonly inheritAriaLabel: (planName: string) => string;
  };
}

const PLANS_CONTENT: Bilingual<PlansContent> = {
  'pt-BR': {
    pageTitle: 'Planos',
    pageDescription: 'Catálogo de planos públicos e planos negociados, sincronizados com o Stripe.',
    newPlanButton: 'Novo plano',
    newTargetedPlanButton: 'Novo plano negociado',
    plansTab: 'Planos',
    negotiatedTab: 'Negociados',
    common: {
      tier: 'Tier',
      tierPlaceholder: 'pro',
      rank: 'Ordem (rank)',
      lookupKey: 'Lookup key',
      capabilities: 'Capabilities',
      cancel: 'Cancelar',
      save: 'Salvar',
      archive: 'Arquivar',
      loadMore: 'Carregar mais',
      searchPlaceholder: 'Buscar por nome, tier ou lookup key…',
      tierFilterLabel: 'Tier',
      interval: 'Intervalo',
      intervalMonthly: 'Mensal',
      intervalYearly: 'Anual',
      statusFilterAriaLabel: 'Filtrar por status',
      all: 'Todos',
      publicToggleLabel: 'Público no app',
      publicToggleDescription:
        'Quando ligado, o plano aparece nas listagens comuns (filtro do marketplace e seleção ' +
        'de plano no detalhe do pack). Desligado, o plano existe e é assinável por link direto, ' +
        'mas não aparece nessas listagens.',
      clearFilters: 'Limpar filtros',
    },
    capabilitiesEditor: {
      'cloud.sync': 'Sincronização na nuvem',
      'ai.tokens': 'Tokens de IA (quota)',
      'history.retention': 'Retenção de histórico (dias)',
      seats: 'Assentos',
      'packs.private': 'Packs privados',
      'collab.realtime': 'Colaboração em tempo real',
    },
    archiveDialog: {
      confirm: 'Arquivar',
    },
    orgPicker: {
      selectPlaceholder: 'Selecionar organização...',
      searchPlaceholder: 'Buscar org por nome...',
      searchAriaLabel: 'Buscar organização',
    },
    editModal: {
      updatedToast: 'Plano atualizado.',
      title: 'Editar plano',
      description:
        'Edite nome, capabilities, tier, ordenação e visibilidade sem alterar a cobrança.',
      immutableNotice:
        'Preço e intervalo não mudam aqui. Para alterá-los, arquive este plano e crie um novo.',
      targetedTitle: 'Editar plano negociado',
      targetedDescription:
        'Atualize os dados operacionais do plano sem trocar seu alvo ou sua cobrança.',
      targetedImmutableNotice:
        'Alvo, lookup key, intervalo e preços não mudam aqui. Para alterá-los, arquive este plano ' +
        'e crie um novo.',
      targetedReasonLabel: 'Motivo (obrigatório ao alterar)',
      targetedReasonError: 'Ao alterar o motivo, informe entre 10 e 500 caracteres.',
    },
    publicTable: {
      capabilitySummary: (count) => `${count} capabilit${count === 1 ? 'y' : 'ies'}`,
      columnPlan: 'Plano',
      columnLookupKey: 'Lookup key',
      columnTier: 'Tier',
      columnVisibility: 'Visibilidade',
      columnPrice: 'Preço',
      columnInterval: 'Intervalo',
      columnCapabilities: 'Capabilities',
      columnActions: 'Ações',
      archivedBadge: 'Arquivado',
      visible: 'Público',
      hidden: 'Oculto',
      editAriaLabel: (planName) => `Editar ${planName}`,
      archiveAriaLabel: (planName) => `Arquivar ${planName}`,
      emptyFilteredTitle: 'Nenhum resultado',
      emptyFilteredDescription: 'Nenhum plano corresponde à busca.',
      emptyTitle: 'Sem planos públicos',
    },
    publicSection: {
      intro:
        'Crie e edite planos públicos aqui — o painel sincroniza com o Stripe (Product + Price) e ' +
        'define as capabilities por plano. O nome editado acompanha o Product; preço e intervalo ' +
        'continuam imutáveis, e assinantes atuais mantêm o preço.',
      searchAriaLabel: 'Buscar planos',
      visibilityFilterAriaLabel: 'Filtrar por visibilidade',
      visibilityPublic: 'Público',
      visibilityHidden: 'Oculto',
      intervalFilterAriaLabel: 'Filtrar por intervalo',
      statusActive: 'Ativos',
      statusArchived: 'Arquivados',
      loadError: 'Falha ao carregar planos.',
      archivedToast: 'Plano arquivado.',
      archiveDialogTitle: 'Arquivar plano',
      archiveDialogDescription: (planName) =>
        `Arquiva “${planName}” e o Price no Stripe. Assinantes atuais mantêm o acesso; o plano ` +
        'deixa de ser oferecido a novos. A lookup key pode ser reutilizada depois.',
      bulkSelected: (count) => `${count} plano(s) ativo(s) selecionado(s)`,
      bulkArchive: 'Arquivar selecionados',
      bulkClear: 'Limpar seleção',
      bulkTitle: 'Arquivar planos selecionados',
      bulkDescription: (count) =>
        `Arquivar ${count} plano(s) ativo(s), em sequência, no catálogo e no Stripe?`,
      bulkConfirm: 'Arquivar planos',
      bulkSuccess: (count) => `${count} plano(s) arquivado(s).`,
      bulkPartial: (success, failed) => `${success} arquivado(s); ${failed} com falha.`,
      bulkError: 'Falha ao arquivar os planos selecionados.',
      selectPageAriaLabel: 'Selecionar planos ativos desta página',
      selectRowAriaLabel: (planName) => `Selecionar plano ${planName}`,
    },
    ...TARGETED_PLANS_CONTENT['pt-BR'],
    formFields: {
      displayName: 'Nome de exibição',
      displayNamePlaceholder: 'Planuze Pro',
      lookupKeyPlaceholder: 'planuze_pro_monthly',
      basePrice: 'Preço base do plano (USD)',
      price: 'Preço (USD)',
      basePriceHint: 'Valor fixo mensal que já inclui os assentos abaixo.',
      seatsIncluded: 'Assentos inclusos na base',
      extraSeatPrice: 'Preço do assento extra (USD)',
      extraSeatHint: 'Cobrado por assento acima dos inclusos. 0 = não permite excedentes.',
      targeting: 'Direcionamento',
      targetType: 'Tipo de alvo',
      organization: 'Organização',
      user: 'Usuário',
      target: 'Alvo',
      validFrom: 'Vale de',
      validUntil: 'Vale até (opcional)',
      reason: 'Motivo (mín. 10 caracteres)',
      inheritTitle: 'Herdar acesso a packages/templates',
      inheritDescription:
        'De quais planos este plano herda o acesso do marketplace a packages e templates. Só ' +
        'isso é herdado — tokens, seats e retenção continuam próprios deste plano.',
      noSourcePlans: 'Nenhum plano público disponível.',
      inheritAriaLabel: (planName) => `Herdar de ${planName}`,
    },
  },
  'en-US': {
    pageTitle: 'Plans',
    pageDescription: 'Catalog of public and negotiated plans, synced with Stripe.',
    newPlanButton: 'New plan',
    newTargetedPlanButton: 'New negotiated plan',
    plansTab: 'Plans',
    negotiatedTab: 'Negotiated',
    common: {
      tier: 'Tier',
      tierPlaceholder: 'pro',
      rank: 'Rank',
      lookupKey: 'Lookup key',
      capabilities: 'Capabilities',
      cancel: 'Cancel',
      save: 'Save',
      archive: 'Archive',
      loadMore: 'Load more',
      searchPlaceholder: 'Search by name, tier, or lookup key…',
      tierFilterLabel: 'Tier',
      interval: 'Interval',
      intervalMonthly: 'Monthly',
      intervalYearly: 'Yearly',
      statusFilterAriaLabel: 'Filter by status',
      all: 'All',
      publicToggleLabel: 'Public in the app',
      publicToggleDescription:
        'When on, the plan shows up in common listings (marketplace filter and plan selection on ' +
        'the pack detail page). When off, the plan still exists and can be subscribed via direct ' +
        'link, but does not appear in those listings.',
      clearFilters: 'Clear filters',
    },
    capabilitiesEditor: {
      'cloud.sync': 'Cloud sync',
      'ai.tokens': 'AI tokens (quota)',
      'history.retention': 'History retention (days)',
      seats: 'Seats',
      'packs.private': 'Private packs',
      'collab.realtime': 'Real-time collaboration',
    },
    archiveDialog: {
      confirm: 'Archive',
    },
    orgPicker: {
      selectPlaceholder: 'Select organization...',
      searchPlaceholder: 'Search org by name...',
      searchAriaLabel: 'Search organization',
    },
    editModal: {
      updatedToast: 'Plan updated.',
      title: 'Edit plan',
      description: 'Edit name, capabilities, tier, rank, and visibility without changing billing.',
      immutableNotice:
        'Price and interval cannot be changed here. To change them, archive this plan and create ' +
        'a new one.',
      targetedTitle: 'Edit negotiated plan',
      targetedDescription:
        'Update operational plan data without changing its target or billing identity.',
      targetedImmutableNotice:
        'Target, lookup key, interval, and prices cannot be changed here. To change them, archive ' +
        'this plan and create a new one.',
      targetedReasonLabel: 'Reason (required when changed)',
      targetedReasonError: 'When changing the reason, enter between 10 and 500 characters.',
    },
    publicTable: {
      capabilitySummary: (count) => `${count} capabilit${count === 1 ? 'y' : 'ies'}`,
      columnPlan: 'Plan',
      columnLookupKey: 'Lookup key',
      columnTier: 'Tier',
      columnVisibility: 'Visibility',
      columnPrice: 'Price',
      columnInterval: 'Interval',
      columnCapabilities: 'Capabilities',
      columnActions: 'Actions',
      archivedBadge: 'Archived',
      visible: 'Public',
      hidden: 'Hidden',
      editAriaLabel: (planName) => `Edit ${planName}`,
      archiveAriaLabel: (planName) => `Archive ${planName}`,
      emptyFilteredTitle: 'No results',
      emptyFilteredDescription: 'No plan matches the search.',
      emptyTitle: 'No public plans',
    },
    publicSection: {
      intro:
        'Create and edit public plans here — the panel syncs with Stripe (Product + Price) and ' +
        'defines the capabilities per plan. An edited name follows the Product; price and ' +
        'interval remain immutable, and current subscribers keep their price.',
      searchAriaLabel: 'Search plans',
      visibilityFilterAriaLabel: 'Filter by visibility',
      visibilityPublic: 'Public',
      visibilityHidden: 'Hidden',
      intervalFilterAriaLabel: 'Filter by interval',
      statusActive: 'Active',
      statusArchived: 'Archived',
      loadError: 'Failed to load plans.',
      archivedToast: 'Plan archived.',
      archiveDialogTitle: 'Archive plan',
      archiveDialogDescription: (planName) =>
        `Archives “${planName}” and its Price in Stripe. Current subscribers keep access; the ` +
        'plan stops being offered to new ones. The lookup key can be reused afterward.',
      bulkSelected: (count) => `${count} active plan(s) selected`,
      bulkArchive: 'Archive selected',
      bulkClear: 'Clear selection',
      bulkTitle: 'Archive selected plans',
      bulkDescription: (count) =>
        `Archive ${count} active plan(s), sequentially, in the catalog and Stripe?`,
      bulkConfirm: 'Archive plans',
      bulkSuccess: (count) => `${count} plan(s) archived.`,
      bulkPartial: (success, failed) => `${success} archived; ${failed} failed.`,
      bulkError: 'Failed to archive the selected plans.',
      selectPageAriaLabel: 'Select active plans on this page',
      selectRowAriaLabel: (planName) => `Select plan ${planName}`,
    },
    ...TARGETED_PLANS_CONTENT['en-US'],
    formFields: {
      displayName: 'Display name',
      displayNamePlaceholder: 'Planuze Pro',
      lookupKeyPlaceholder: 'planuze_pro_monthly',
      basePrice: 'Plan base price (USD)',
      price: 'Price (USD)',
      basePriceHint: 'Fixed monthly amount that already includes the seats below.',
      seatsIncluded: 'Seats included in the base',
      extraSeatPrice: 'Extra seat price (USD)',
      extraSeatHint: 'Charged per seat above the included ones. 0 = no overage allowed.',
      targeting: 'Targeting',
      targetType: 'Target type',
      organization: 'Organization',
      user: 'User',
      target: 'Target',
      validFrom: 'Valid from',
      validUntil: 'Valid until (optional)',
      reason: 'Reason (min. 10 characters)',
      inheritTitle: 'Inherit access to packages/templates',
      inheritDescription:
        'Which plans this plan inherits marketplace access to packages and templates from. Only ' +
        'that is inherited — tokens, seats, and retention remain this plan’s own.',
      noSourcePlans: 'No public plan available.',
      inheritAriaLabel: (planName) => `Inherit from ${planName}`,
    },
  },
};

export const usePlansContent = (): PlansContent => useBilingual(PLANS_CONTENT);
